import { useIntl } from 'react-intl';
import { useEffect, useMemo, useReducer } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import parse from 'html-react-parser';

import Grid from '@material-ui/core/Grid';

import { DynoAlertModal } from 'components/dyno-template/modals';
import AccountSelectionSection from 'components/transfer/AccountSelectionSection';

import {
  addNewFavouriteDispatchHandler,
  addNewFavouriteFromTransferDetail,
  editFavouriteSubmitFormHandler,
  favouriteDetailHandler,
  favouriteConfirmationDetailHandler,
  favouriteFormDetailHandler,
  favouriteResultDetailHandler,
  favouriteTacDetailHandler,
} from 'utils/favourite/transfer/handler';
import { get } from 'utils/lodash';
import { favDetailHandler } from 'utils/transfer/handler';
import { formatReceiverAccounts } from 'utils/transfer/formatter';
import {
  isOperationHour,
  getOperationHour,
  getSenderAccountValueFromDashboard,
  getBeforeStartTime,
  getTacRequestDetail,
} from 'utils/transaction';
import { getSelectedReceiverAccount } from 'utils/transfer/own-account/getter';
import { getConfirmationDetail, getAlertTransferName, getSearchableFavouriteAccounts, getFavouriteFormValue } from 'utils/transfer/getter';
import { getNextBusinessDay, getRemainingDuration, getTimeByFormat } from 'utils/date-time';
import { putSelectedReceiverAccount, getSwiftBank, getSwiftBankDetail } from 'middleware/actions/transfer';
import {
  getSwiftDetail,
  getSwiftSearchResult,
  putResetAddFavouriteTransfer,
  putResetFavouriteTransfer,
  putSwiftSearchResult,
} from 'middleware/actions/favourite/transfer';

import {
  OWN_ACCOUNT,
  SKN_TRANSFER,
  RTGS_TRANSFER,
  TRANSFER_FUTURE,
  CUTOFF_TIME_FORMAT,
  IDR_ONLY_TRANSFERS,
  HOLIDAY_DATE_FORMAT,
  DEFAULT_SENDER_ACCOUNT,
  WITHOUT_BANK_SELECTIONS,
  DEFAULT_TRANSFER_TYPE_VALUE,
  SWIFT_TRANSFER,
  TRANSACTION_TYPE,
  E_WALLET,
  MULTICURRENCY_IDR_ONLY_TRANSFERS,
} from 'settings/constants/transaction';
import { TODAY } from 'settings/constants/calendar';
import { TRANSFER_FORM_INPUT, FAVOURITE_TRANSFER_INPUT } from 'settings/constants/input-fields';
import {
  FAVOURITE_ADD_NEW,
  CURRENCY_IDR,
  FAVOURITE_EDIT,
  FAVOURITE_SELECTION,
  ERR_44,
  MULTICURRENCY_PRODUCT_TYPE,
} from 'settings/constants/common';

import TransferModal from './Modal';
import FavouriteModal from './FavouriteModal';
import FavouriteConfirmation from './FavouriteConfirmation';
import TransferConfirmationContainer from './Confirmation';

import './style.scss';

const { TRANSFER } = TRANSACTION_TYPE;

const TransferContainer = ({
  onChange,
  userId,
  swiftBank,
  scrollToRef,
  transferType,
  transferTypes,
  transferDetail,
  senderAccounts,
  isSwiftOffline,
  isServiceOnline,
  swiftBankDetail,
  setTacRequested,
  favouriteAccount,
  isBackToMainPage,
  onAccountRefresh,
  onCancelTransfer,
  transferTacDetail,
  accountNoFromCasa,
  onErrorDrawerOpen,
  isErrorDrawerOpen,
  swiftOperationTime,
  confirmationDetail,
  favouriteSwiftBanks,
  isTransferConfirmed,
  onTransferTypeChange,
  transferResultDetail,
  isTransactionBlocked,
  receiverAccountResult,
  initialSenderAccounts,
  isSwiftLldDetailSuccess,
  handleTutorial,
  handleCloseCoachmark,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const {
    maintenanceTransfers,
    intrabankFormDetail,
    intrabankFavouriteConfirmation,
    intrabankFavouriteTac,
    intrabankFavouriteResult,
    virtualAccountFavouriteDetail,
    virtualAccountFavouriteConfirmation,
    virtualAccountFavouriteTac,
    virtualAccountFavouriteResult,
    eWalletFavouriteDetail,
    eWalletFavouriteConfirmation,
    eWalletFavouriteTac,
    eWalletFavouriteResult,
    rtgsFavouriteDetail,
    rtgsFavouriteConfirmation,
    rtgsFavouriteTac,
    rtgsFavouriteResult,
    sknFavouriteDetail,
    sknFavouriteConfirmation,
    sknFavouriteTac,
    sknFavouriteResult,
    interbankFavouriteDetail,
    interbankFavouriteConfirmation,
    interbankFavouriteTac,
    interbankFavouriteResult,
    swiftFavouriteDetail,
    swiftFavouriteConfirmation,
    swiftFavouriteTac,
    swiftFavouriteResult,
    favouriteReducer,
  } = useSelector(
    state => ({
      favouriteAccount: state.FavouriteTransferReducer.favouriteAccount,
      maintenanceTransfers: state.FavouriteTransferReducer.maintenanceTransfers,
      intrabankFormDetail: state.FavouriteTransferReducer.intrabankFormDetail,
      intrabankFavouriteConfirmation: state.FavouriteTransferReducer.intrabankConfirmation,
      intrabankFavouriteTac: state.FavouriteTransferReducer.intrabankTac,
      intrabankFavouriteResult: state.FavouriteTransferReducer.intrabankResult,
      virtualAccountFavouriteDetail: state.FavouriteTransferReducer.virtualAccountFavouriteDetail,
      virtualAccountFavouriteConfirmation: state.FavouriteTransferReducer.virtualAccountConfirmation,
      virtualAccountFavouriteTac: state.FavouriteTransferReducer.virtualAccountTac,
      virtualAccountFavouriteResult: state.FavouriteTransferReducer.virtualAccountResult,
      eWalletFavouriteDetail: state.FavouriteTransferReducer.eWalletFavouriteDetail,
      eWalletFavouriteConfirmation: state.FavouriteTransferReducer.eWalletConfirmation,
      eWalletFavouriteTac: state.FavouriteTransferReducer.eWalletTac,
      eWalletFavouriteResult: state.FavouriteTransferReducer.eWalletResult,
      rtgsFavouriteDetail: state.FavouriteTransferReducer.rtgsFavouriteDetail,
      rtgsFavouriteConfirmation: state.FavouriteTransferReducer.rtgsConfirmation,
      rtgsFavouriteTac: state.FavouriteTransferReducer.rtgsTac,
      rtgsFavouriteResult: state.FavouriteTransferReducer.rtgsResult,
      sknFavouriteDetail: state.FavouriteTransferReducer.sknFavouriteDetail,
      sknFavouriteConfirmation: state.FavouriteTransferReducer.sknConfirmation,
      sknFavouriteTac: state.FavouriteTransferReducer.sknTac,
      sknFavouriteResult: state.FavouriteTransferReducer.sknResult,
      interbankFavouriteConfirmation: state.FavouriteTransferReducer.interbankConfirmation,
      interbankFavouriteTac: state.FavouriteTransferReducer.interbankTac,
      interbankFavouriteResult: state.FavouriteTransferReducer.interbankResult,
      interbankFavouriteDetail: state.FavouriteTransferReducer.interbankFavouriteDetail,
      swiftFavouriteDetail: state.FavouriteTransferReducer.swiftFavouriteDetail,
      swiftFavouriteConfirmation: state.FavouriteTransferReducer.swiftConfirmation,
      swiftFavouriteTac: state.FavouriteTransferReducer.swiftTac,
      swiftFavouriteResult: state.FavouriteTransferReducer.swiftResult,
      favouriteReducer: state.FavouriteTransferReducer,
    }),
    shallowEqual
  );

  const favouriteAccounts = get(favouriteAccount, 'favouriteAccounts', []);
  const holidays = get(transferDetail, 'holidays', []);
  const serviceInfo = get(transferDetail, 'serviceInfoBean', {});
  const isSknRtgsTransfer = [RTGS_TRANSFER, SKN_TRANSFER].includes(transferType);
  const isOperatingHour = isOperationHour(serviceInfo, holidays, isSknRtgsTransfer);
  const { startTime, endTime } = getOperationHour(serviceInfo);
  const isTransferNowBlocked = !isOperatingHour && isSknRtgsTransfer;
  // time between 0000 and start time
  const isSknRtgsBeforeStartTime = getBeforeStartTime(startTime);

  const duration = getRemainingDuration({
    startTime: getTimeByFormat(moment(), CUTOFF_TIME_FORMAT),
    endTime: moment(endTime, CUTOFF_TIME_FORMAT),
    unit: 'minutes',
  });

  // If transfer during non operationHour, set to Later and tomorrow's date (for SKN & RTGS only)
  const initialState = !isTransferNowBlocked
    ? TRANSFER_FORM_INPUT
    : {
        ...TRANSFER_FORM_INPUT,
        isToday: false,
        transferMode: TRANSFER_FUTURE,
        transferDate: getNextBusinessDay(TODAY, holidays, HOLIDAY_DATE_FORMAT),
      };
  const initialTransferDate = get(initialState, 'transferDate', TODAY);

  // NOTE: All state should be initialized here and passed down to form modals
  const [state, setState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    ...initialState,
    ...FAVOURITE_TRANSFER_INPUT,
    transactionType: TRANSFER,
    transferFrom: DEFAULT_SENDER_ACCOUNT,
    isDeleteFavourite: false,
    beneficiaryBank: {},
    isFormModalOpen: false,
    isNoticeOpen: false,
    searchValue: '',
    isAddNewFavourite: false,
    favouriteTransferType: '',
    isFavouriteModalOpen: false,
    isSwiftBankSearch: false,
    isFavouriteTransactionSuccess: false,
    isFavouriteTransactionCompleted: false,
    isDisplayTransferResult: true,
    isFavouriteTransfer: false,
    // isLLD: false,
  });

  const {
    favouriteIndex,
    transactionType,
    isAddNewFavourite,
    isDeleteFavourite,
    transferFrom,
    transferMode,
    transferFrequency,
    beneficiaryBank,
    beneficiaryType,
    beneficiaryCitizenStatus,
    isFormModalOpen,
    searchValue,
    isNoticeOpen,
    transactionCharges,
    isFavouriteModalOpen,
    favouriteTransferType,
    isSwiftBankSearch,
    isFavouriteTransactionSuccess,
    isFavouriteTransactionCompleted,
    isDisplayTransferResult,
    isFavouriteTransfer,
    // isLLD,
  } = state;

  /* Start Favourite Section handler & indicator */
  const favouriteDetail = favouriteDetailHandler(favouriteTransferType, {
    intrabankFormDetail,
    virtualAccountFavouriteDetail,
    sknFavouriteDetail,
    rtgsFavouriteDetail,
    eWalletFavouriteDetail,
    interbankFavouriteDetail,
    swiftFavouriteDetail,
  });
  const favouriteConfirmationDetail = favouriteConfirmationDetailHandler(favouriteTransferType, {
    intrabankFavouriteConfirmation,
    virtualAccountFavouriteConfirmation,
    eWalletFavouriteConfirmation,
    rtgsFavouriteConfirmation,
    sknFavouriteConfirmation,
    interbankFavouriteConfirmation,
    swiftFavouriteConfirmation,
  });
  const favouriteTacDetail = favouriteTacDetailHandler(favouriteTransferType, {
    intrabankFavouriteTac,
    virtualAccountFavouriteTac,
    eWalletFavouriteTac,
    rtgsFavouriteTac,
    sknFavouriteTac,
    interbankFavouriteTac,
    swiftFavouriteTac,
  });
  const favouriteResultDetail = favouriteResultDetailHandler(favouriteTransferType, {
    intrabankFavouriteResult,
    virtualAccountFavouriteResult,
    eWalletFavouriteResult,
    rtgsFavouriteResult,
    sknFavouriteResult,
    interbankFavouriteResult,
    swiftFavouriteResult,
  });

  const isSwiftFavouriteType = favouriteTransferType === SWIFT_TRANSFER;
  const isFavouriteTacSuccess = get(favouriteTacDetail, 'isSuccess', false);
  const isEditFavourite = get(favouriteDetail, 'isEditFavourite', false);
  const isAddNewDetailSuccess = get(favouriteDetail, 'isSuccess', false);
  const isFromTransferResult = get(favouriteDetail, 'isFromTransferResult', false);
  const isFavouriteConfirmSuccess = get(favouriteConfirmationDetail, 'isSuccess', false);
  const isEditFavouriteDetailSuccess = isEditFavourite && isAddNewDetailSuccess;
  /* End Favourite Section handler & indicator */

  // senderAccounts = transferFrom
  const senderAccountValue = !isEmpty(senderAccounts) ? transferFrom : '';
  const senderAccount = senderAccountValue ? senderAccounts.find(account => account.value === senderAccountValue) : {};
  const senderCurrency = get(senderAccount, 'currency', CURRENCY_IDR);
  const senderProductCode = get(senderAccount, 'productCode', '');

  // receiverAccounts = Own Account list
  const initialReceiverAccounts = get(receiverAccountResult, 'receiverAccounts', []);
  const receiverAccounts = formatReceiverAccounts(initialReceiverAccounts);

  // transfer types that directly prompt a modal after selection
  const hasBankSelection = !WITHOUT_BANK_SELECTIONS.includes(transferType);

  const isSenderIDR = senderCurrency === CURRENCY_IDR;
  const isTransferSuccess = get(transferDetail, 'isSuccess', false);
  const isTransferIDROnly = !MULTICURRENCY_PRODUCT_TYPE.includes(senderProductCode)
    ? IDR_ONLY_TRANSFERS.includes(transferType)
    : MULTICURRENCY_IDR_ONLY_TRANSFERS.includes(transferType);
  const isSwiftBankDetailSuccess = get(swiftBankDetail, 'isSuccess', false);
  const swiftBankDetailError = get(swiftBankDetail, 'errorCode', '');

  const transferName = getAlertTransferName(transferType, formatMessage);
  const sknRtgsNoticeMessage = isSknRtgsBeforeStartTime
    ? 'transaction.cutoffAlert.sknRtgsTodayDescription'
    : 'transaction.cutoffAlert.sknRtgsDescription';
  const sknRtgsNoticeTitle = isSknRtgsBeforeStartTime ? 'transaction.cutoffAlert.operationHours' : 'transaction.cutoffAlert.title';

  const { confirmError } = getConfirmationDetail(confirmationDetail, transferType);

  const favDetail = favDetailHandler(favouriteReducer);

  const favConfirmationDetail = favDetail[favouriteTransferType] || favDetail.default;
  const favConfirmationResponse = getConfirmationDetail(favConfirmationDetail, favouriteTransferType);
  const favConfirmError = get(favConfirmationResponse, 'confirmError', '');

  // transfer detail from add favourite after transfer result
  const favouriteFromTransferDetail = addNewFavouriteFromTransferDetail(favouriteTransferType, favouriteDetail, isEditFavourite, state);

  // Handle TAC error response
  const transferTacRequestError = get(transferTacDetail, 'errorCode', '');
  const favouriteTacRequestError = get(favouriteTacDetail, 'errorCode', '');
  const favouriteDetailError = get(favouriteDetail, 'errorCode', '');

  // indicator to scroll to top after submitting tac
  const isTransferResultTransacted = !isEmpty(get(transferResultDetail, 'responseCode', ''));
  const isFavouriteResultTransacted = !isEmpty(get(favouriteResultDetail, 'responseCode', ''));

  const { isTacRequestSuccess } = useMemo(() => getTacRequestDetail(transferTacDetail), [transferTacDetail]);

  const resetTransferType = () => onTransferTypeChange(DEFAULT_TRANSFER_TYPE_VALUE);

  const handleNoticeClose = () => setState({ isNoticeOpen: false });

  const handleModalOpen = () => setState({ isFormModalOpen: true });

  const handleTransferTypeChange = (type, favIndex) => onTransferTypeChange(type, isSenderIDR, favIndex, senderProductCode);

  const handleModalClose = (isReset = true) => {
    if (isReset && !isTransferConfirmed) {
      // NOTE: dont clear after click Transfer
      onTransferTypeChange('');
      setState({ ...initialState, beneficiaryBank: {}, isFavouriteTransfer: false });
    }
    setState({ isFormModalOpen: false });
  };

  const handleCutoffPopupClose = (isReset = true) => {
    if (isReset && !isTransferConfirmed) {
      // NOTE: dont clear after click Transfer
      onTransferTypeChange('');
      setState({ ...initialState, beneficiaryBank: {}, isFavouriteTransfer: false });
    }
    setState({ isFormModalOpen: false });
    setState({ isNoticeOpen: false });
  };

  const handleOwnAccountChange = value => {
    const selectedReceiverAccount = getSelectedReceiverAccount(initialReceiverAccounts, String(value));
    const accountNumber = get(selectedReceiverAccount, 'accountNo', '');
    const currency = get(selectedReceiverAccount, 'currencyCodeIso', '');
    // avoid passing props to TransferModal
    dispatch(putSelectedReceiverAccount(selectedReceiverAccount));

    setState({ isFormModalOpen: true, accountNumber, currency });
  };

  const handleSearchBank = ({ bankName = '', bankSwiftCode = '' }) => {
    const payload = { userId, bankName, swiftCode: bankSwiftCode };
    onChange({ bankName, bankSwiftCode });
    dispatch(getSwiftBank(payload));
  };

  const handleSwiftCodeClick = item => {
    const index = get(item, 'index', '');
    const bankMap = get(swiftBank, 'bankMap', {});

    dispatch(getSwiftBankDetail({ index, bankMap, userId }));
  };

  const handleFavouriteSwiftBankClick = item => {
    const oid = get(item, 'oid', '');
    dispatch(getSwiftDetail({ userId, oid }));
  };

  // Favourite section
  // For delete favourite
  const handleFavouriteTypeChange = (type, isDeleteFavouriteFlag) =>
    setState({ ...state, favouriteTransferType: type, ...isDeleteFavouriteFlag });

  const handleAddFavourite = (bool = false) => setState({ isAddNewFavourite: bool, isFavouriteModalOpen: true });

  const handleFavouriteClose = () => {
    const isEdit = !isEmpty(favouriteConfirmationDetail) && isFavouriteConfirmSuccess;
    // if not edit clear all data
    if (!isEdit) {
      dispatch(putResetAddFavouriteTransfer());
      setState({ ...FAVOURITE_TRANSFER_INPUT });
    }
    setState({ isFavouriteModalOpen: false, favouriteTransferType: isEdit ? favouriteTransferType : '' });
  };

  // handle favourite form action
  const handleSubmitFavourite = ({ swiftBankPayload, action }) => {
    const actionHandler = {
      [FAVOURITE_SELECTION]: () => favouriteFormDetailHandler(favouriteTransferType, userId, setState),
      [FAVOURITE_ADD_NEW]: () => {
        // isDisplayTransferResult = false: dont display result page when add new fav from transfer
        if (isFromTransferResult) setState({ isDisplayTransferResult: !favConfirmError });
        return addNewFavouriteDispatchHandler(favouriteTransferType, { ...state, senderAccount, userId, isFromTransferResult });
      },
      [SWIFT_TRANSFER]: () => {
        dispatch(putSwiftSearchResult());
        dispatch(getSwiftSearchResult({ userId, ...swiftBankPayload }));
      },
      [FAVOURITE_EDIT]: () => editFavouriteSubmitFormHandler(favouriteTransferType, { userId, ...state }),
      default: () => {},
    };

    return (actionHandler[action] || actionHandler.default)();
  };

  const handleTransferFromFavourite = (type, selectedFavourite) => {
    const favIndex = get(selectedFavourite, 'index', '');

    // type returned from backend are integer
    handleTransferTypeChange(String(type), String(favIndex));
    // handleModalOpen();
    setState({ favouriteIndex: String(favIndex), isFavouriteTransfer: true });
    // next tep will go to `favouriteFormValue`
  };

  // if user navigate from CASA dashboard, re assign 'transferFrom' to selected source account
  useEffect(() => {
    if (accountNoFromCasa) {
      getSenderAccountValueFromDashboard(senderAccounts, accountNoFromCasa, 'transferFrom', setState);
    }
    // dispatch(getCustomerDetail({ userId }));
  }, []);

  useEffect(() => {
    // open transfer form depends on isTransferSuccess
    // for open transfer, if transfer type have no autocomplete field and service is online, then open the modal
    // for fav transfer, if isFavouriteTransfer, favouriteIndex, isServiceOnline true, then open the modal
    if ((!hasBankSelection && isServiceOnline) || (isFavouriteTransfer && String(favouriteIndex) && isServiceOnline))
      setState({ isFormModalOpen: isTransferSuccess });
    if (!isEmpty(transferDetail) && isFavouriteTransfer && !isTransferSuccess) {
      setState({ isFavouriteTransfer: false, isFormModalOpen: false });
    }
  }, [isTransferSuccess, transferDetail]);

  useEffect(() => {
    // only open swift form modal if bank detail success and swift is online
    setState({ isFormModalOpen: isSwiftBankDetailSuccess && !isSwiftOffline });
  }, [isSwiftBankDetailSuccess, swiftBankDetail, isSwiftOffline]);

  useEffect(() => {
    // if non operationHour, show Cutoff time notice
    // notice only shows for skn/rtgs. for swift, will show error drawer instead
    if (!isEmpty(serviceInfo) && (isTransferNowBlocked || isSknRtgsBeforeStartTime) && [SKN_TRANSFER, RTGS_TRANSFER].includes(transferType))
      setState({ isNoticeOpen: !(MULTICURRENCY_PRODUCT_TYPE.includes(senderProductCode) && senderCurrency !== CURRENCY_IDR) });
  }, [serviceInfo, isTransferNowBlocked]);

  useEffect(() => {
    if (isTransferConfirmed) {
      handleModalClose(false);
    }
  }, [isTransferConfirmed, confirmationDetail]);

  useEffect(() => {
    if (swiftBankDetailError) {
      onErrorDrawerOpen(formatMessage({ id: `errorMessages.ERR_${swiftBankDetailError}` }));
    }
  }, [swiftBankDetail, swiftBankDetailError]);

  useEffect(() => {
    if (confirmError) {
      onErrorDrawerOpen(parse(formatMessage({ id: `errorMessages.ERR_${confirmError}` }, swiftOperationTime)));
    }
  }, [confirmError, confirmationDetail]);

  useEffect(() => {
    if (favConfirmError) {
      const errorMessage =
        favConfirmError === ERR_44 && favouriteTransferType === E_WALLET ? `${favConfirmError}_EWALLET` : favConfirmError;
      onErrorDrawerOpen(parse(formatMessage({ id: `errorMessages.ERR_${errorMessage}` })));
    }
  }, [favConfirmError, favConfirmationDetail]);

  useEffect(() => {
    if (transferTacRequestError || favouriteTacRequestError) {
      onErrorDrawerOpen(formatMessage({ id: `errorMessages.ERR_${transferTacRequestError || favouriteTacRequestError}` }));
    }
  }, [transferTacRequestError, favouriteTacRequestError]);

  useEffect(() => {
    if (favouriteDetailError) {
      onErrorDrawerOpen(formatMessage({ id: `errorMessages.ERR_${favouriteDetailError}` }));
    }
  }, [favouriteDetailError]);

  // isBackToMainPage resets transfer
  useEffect(() => {
    if (isBackToMainPage) {
      resetTransferType();
      setState({ ...initialState, ...FAVOURITE_TRANSFER_INPUT });
    }
  }, [isBackToMainPage]);

  useEffect(() => {
    // OWN_TRANSFER: avoid accountNumber, currency to be cleared (when select account will trigger changes to transferDetail)
    const accountNumber = transferType === OWN_ACCOUNT ? get(state, 'accountNumber', '') : get(initialState, 'accountNumber', '');
    const currency = transferType === OWN_ACCOUNT ? get(state, 'currency', '') : get(initialState, 'currency', '');

    // if operatingHour changes, update form values
    if (transferType === OWN_ACCOUNT) setState({ accountNumber, currency });
    if (isSknRtgsTransfer || transferType === SWIFT_TRANSFER) setState({ ...initialState });
  }, [isOperatingHour, transferDetail]);

  // autofill form onclick favourite account
  useEffect(() => {
    // Avoid sending favIndex to API when it's normal transfer
    if (!isNull(favouriteIndex)) {
      if (isSwiftLldDetailSuccess) return;
      const favouriteFormValue = getFavouriteFormValue(transferDetail, transferType, swiftBankDetail);

      if (!isEmpty(favouriteFormValue)) setState({ ...favouriteFormValue });
    }
  }, [transferDetail, swiftBankDetail, isSwiftLldDetailSuccess]);

  useEffect(() => {
    // if source currency is not IDR on VIRTUAL ACCOUNT and INTERBANK prompt error
    if (!isSenderIDR && isTransferIDROnly) {
      onErrorDrawerOpen(formatMessage({ id: 'transaction.transfer.errorMessage.localCurrencyTransaction' }));
    }
  }, [transferType]);

  // reset transferType when transferFrom changed
  useEffect(() => resetTransferType(), [transferFrom]);

  useEffect(() => {
    if (isFavouriteConfirmSuccess && isFavouriteModalOpen) {
      setState({ isFavouriteModalOpen: false });
    }
  }, [favouriteConfirmationDetail]);

  useEffect(() => {
    if (isEditFavouriteDetailSuccess) {
      setState({ isFavouriteModalOpen: true });
    }
  }, [isEditFavouriteDetailSuccess]);

  useEffect(() => {
    // set state (populate form) from detail response for add new favourite at result page
    if (isAddNewDetailSuccess && (isFromTransferResult || isSwiftFavouriteType || isEditFavourite)) {
      setState({ ...favouriteFromTransferDetail });
    }

    // close favourite swift modal after successful detail
    if (isAddNewDetailSuccess && isSwiftFavouriteType) {
      setState({ isSwiftBankSearch: !isAddNewDetailSuccess });
    }
  }, [isFromTransferResult, favouriteDetail, isAddNewDetailSuccess]);

  useEffect(() => {
    if (!isFavouriteTransactionCompleted) {
      return;
    }

    const favouriteTransactionType = isFavouriteTransactionSuccess ? 'Success' : 'Error';
    const drawerType = favouriteTransactionType.toLowerCase();

    if (isEditFavourite && !isDeleteFavourite) {
      onErrorDrawerOpen(parse(formatMessage({ id: `transaction.favourite.drawer.mainPageEdit${favouriteTransactionType}` })), drawerType);
    }
    if (isDeleteFavourite && !isEditFavourite) {
      onErrorDrawerOpen(parse(formatMessage({ id: `transaction.favourite.drawer.mainPageDelete${favouriteTransactionType}` })), drawerType);
    }
    if (!isEditFavourite && !isDeleteFavourite) {
      onErrorDrawerOpen(parse(formatMessage({ id: `transaction.favourite.drawer.mainPageAdd${favouriteTransactionType}` })), drawerType);
    }

    // #4301: isEditFavourite came from redux, so need to clear it after drawermessage have display message
    dispatch(putResetFavouriteTransfer());
    setState({
      isFavouriteTransactionCompleted: false,
      isFavouriteTransactionSuccess: false,
      ...initialState,
      ...FAVOURITE_TRANSFER_INPUT,
      searchValue: '',
      isDeleteFavourite: false,
    });
  }, [isFavouriteTransactionCompleted]);

  useEffect(() => {
    if (isTransferResultTransacted || isFavouriteResultTransacted) {
      scrollToRef();
    }
  }, [transferResultDetail, favouriteResultDetail, isTransferResultTransacted, isFavouriteResultTransacted]);

  // check if tac has been requested. to display prompt
  useEffect(() => {
    if (!isEmpty(transferTacDetail)) {
      setTacRequested(!isEmpty(transferResultDetail) ? false : isTacRequestSuccess);
    }
  }, [transferTacDetail, transferResultDetail]);

  // check if favourite tac has been requested. to display prompt
  useEffect(() => {
    if (!isEmpty(favouriteTacDetail)) {
      setTacRequested(!isEmpty(favouriteResultDetail) ? false : isFavouriteTacSuccess);
    }
  }, [favouriteTacDetail, favouriteResultDetail]);

  // useEffect(() => {
  //   if ((transferType === SWIFT_TRANSFER || transferType === OWN_ACCOUNT) && state.transferAmount > 10000 && state.currency === 'USD') {
  //     setState({ isLLD: true });
  //   } else {
  //     setState({ isLLD: false });
  //   }
  // }, [state.transferAmount, state.currency, transferType]);

  return (
    <Grid container justify="center" className="transaction transfer--container">
      {isTransferConfirmed && isDisplayTransferResult && !isFavouriteConfirmSuccess && (
        <TransferConfirmationContainer
          userId={userId}
          formDetail={state}
          onChange={setState}
          onEdit={handleModalOpen}
          transferType={transferType}
          onError={onErrorDrawerOpen}
          transferMode={transferMode}
          senderAccount={senderAccount}
          beneficiaryType={beneficiaryType}
          onCancelTransfer={onCancelTransfer}
          onAccountRefresh={onAccountRefresh}
          onAddFavourite={handleAddFavourite}
          transferFrequency={transferFrequency}
          transferTacDetail={transferTacDetail}
          favouriteAccounts={favouriteAccounts}
          transactionCharges={transactionCharges}
          confirmationDetail={confirmationDetail}
          swiftOperationTime={swiftOperationTime}
          isFavouriteTransfer={isFavouriteTransfer}
          transferResultDetail={transferResultDetail}
          favouriteTransferType={favouriteTransferType}
          beneficiaryCitizenStatus={beneficiaryCitizenStatus}
          handleTutorial={handleTutorial}
          handleCloseCoachmark={handleCloseCoachmark}
        />
      )}

      {isFavouriteConfirmSuccess && !isFavouriteTransactionCompleted && (
        <FavouriteConfirmation
          userId={userId}
          formDetail={state}
          onChange={setState}
          onEdit={handleAddFavourite}
          onError={onErrorDrawerOpen}
          tacDetail={favouriteTacDetail}
          isEditFavourite={isEditFavourite}
          onCancelTransfer={onCancelTransfer}
          resultDetail={favouriteResultDetail}
          isTacSuccess={isFavouriteTacSuccess}
          isDeleteFavourite={isDeleteFavourite}
          isAddNewFavourite={isAddNewFavourite}
          resetTransferType={resetTransferType}
          isFromTransferResult={isFromTransferResult}
          isConfirmSuccess={isFavouriteConfirmSuccess}
          favouriteTransferType={favouriteTransferType}
          confirmationDetail={favouriteConfirmationDetail}
          handleTutorial={handleTutorial}
          handleCloseCoachmark={handleCloseCoachmark}
        />
      )}
      {!isTransferConfirmed && !isFavouriteConfirmSuccess && (
        <>
          <AccountSelectionSection
            userId={userId}
            onChange={setState}
            swiftBank={swiftBank}
            searchValue={searchValue}
            transferType={transferType}
            // to remove selected value form fav list if swift out of operation times, user isDrawerOpen from Transfer page
            isFormOpen={isSwiftOffline && isFavouriteTransfer ? isErrorDrawerOpen : isFormModalOpen}
            transferTypes={transferTypes}
            onSearchBank={handleSearchBank}
            senderAccount={senderAccount}
            senderAccounts={senderAccounts}
            transferDetail={transferDetail}
            transactionType={transactionType}
            isServiceOnline={isServiceOnline}
            onShowFormModal={handleModalOpen}
            onCodeClick={handleSwiftCodeClick}
            favouriteAccount={favouriteAccount}
            receiverAccounts={receiverAccounts}
            onAddFavourite={handleAddFavourite}
            senderAccountValue={senderAccountValue}
            isTransactionBlocked={isTransactionBlocked}
            onOwnAccountChange={handleOwnAccountChange}
            favouriteTransferType={favouriteTransferType}
            onTransferTypeChange={handleTransferTypeChange}
            onFavouriteTypeChange={handleFavouriteTypeChange}
            onTransferFromFavourite={handleTransferFromFavourite}
          />

          <DynoAlertModal
            hasCancelButton
            onCancel={handleCutoffPopupClose}
            isOpen={isNoticeOpen}
            onSubmit={handleNoticeClose}
            title={formatMessage({ id: sknRtgsNoticeTitle })}
            description={parse(formatMessage({ id: sknRtgsNoticeMessage }, { transferName, startTime, endTime }))}
          />
        </>
      )}
      <TransferModal
        userId={userId}
        formDetail={state}
        duration={duration}
        onChange={setState}
        onOpen={handleModalOpen}
        onClose={handleModalClose}
        onError={onErrorDrawerOpen}
        transferType={transferType}
        isFormOpen={isFormModalOpen}
        senderAccount={senderAccount}
        transferDetail={transferDetail}
        swiftBankDetail={swiftBankDetail}
        beneficiaryBank={beneficiaryBank}
        confirmationDetail={confirmationDetail}
        initialTransferDate={initialTransferDate}
        isFavouriteTransfer={isFavouriteTransfer}
        isTransferNowBlocked={isTransferNowBlocked}
        initialSenderAccounts={initialSenderAccounts}
        receiverAccountResult={receiverAccountResult}
      />

      <FavouriteModal
        userId={userId}
        onChange={setState}
        formDetail={state}
        favouriteDetail={favouriteDetail}
        onAddFavourite={handleAddFavourite}
        isSwiftBankSearch={isSwiftBankSearch}
        onSubmitFavourite={handleSubmitFavourite}
        favouriteSwiftBanks={favouriteSwiftBanks}
        onAddFavouriteClose={handleFavouriteClose}
        isFavouriteModalOpen={isFavouriteModalOpen}
        isFromTransferResult={isFromTransferResult}
        maintenanceTransfers={maintenanceTransfers}
        isAddNewDetailSuccess={isAddNewDetailSuccess}
        favouriteTransferType={favouriteTransferType}
        onSwiftCodeClick={handleFavouriteSwiftBankClick}
        isEditFavouriteDetailSuccess={isEditFavouriteDetailSuccess}
      />
    </Grid>
  );
};

TransferContainer.defaultProps = {
  swiftBank: {},
  favouriteAccount: {},
  favouriteSwiftBanks: {},
  isErrorDrawerOpen: false,
  handleTutorial: null,
  handleCloseCoachmark: null,
};

TransferContainer.propTypes = {
  swiftBank: PropTypes.object,
  favouriteAccount: PropTypes.object,
  favouriteSwiftBanks: PropTypes.object,
  isErrorDrawerOpen: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  scrollToRef: PropTypes.func.isRequired,
  isSwiftOffline: PropTypes.bool.isRequired,
  setTacRequested: PropTypes.func.isRequired,
  onCancelTransfer: PropTypes.func.isRequired,
  swiftOperationTime: PropTypes.object.isRequired,
  swiftBankDetail: PropTypes.object.isRequired,
  transferTypes: PropTypes.array.isRequired,
  accountNoFromCasa: PropTypes.string.isRequired,
  isServiceOnline: PropTypes.bool.isRequired,
  isTransferConfirmed: PropTypes.bool.isRequired,
  isBackToMainPage: PropTypes.bool.isRequired,
  onAccountRefresh: PropTypes.func.isRequired,
  onTransferTypeChange: PropTypes.func.isRequired,
  onErrorDrawerOpen: PropTypes.func.isRequired,
  receiverAccountResult: PropTypes.object.isRequired,
  senderAccounts: PropTypes.array.isRequired,
  initialSenderAccounts: PropTypes.array.isRequired,
  transferResultDetail: PropTypes.object.isRequired,
  confirmationDetail: PropTypes.object.isRequired,
  transferDetail: PropTypes.object.isRequired,
  transferTacDetail: PropTypes.object.isRequired,
  isTransactionBlocked: PropTypes.bool.isRequired,
  isSwiftLldDetailSuccess: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  transferType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleTutorial: PropTypes.func,
  handleCloseCoachmark: PropTypes.func,
};

export default TransferContainer;
